import {
  Flex,
  NestedContentItem,
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportTableCell,
  type ReportTableProps,
} from '@/components';
import { useAccountingTypeOptions } from '@/features/master-table/hooks';
import type { LedgerKsAdditionalInfoSectionProps } from '@/features/mold/models/ledger';
import {
  FundBudgetClassificationModifier,
  FundBudgetTypeLabelModifier,
  type FundCompositionBackgroundsModel,
  MeansOfDeliveryLabelModifier,
  fundBudgetType,
} from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import {
  FALLBACK_TEXT,
  NEST_ITEM_CONNECTOR,
  formatExistenceValue,
  formatValueByUnit,
} from '@/utils';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  LEDGER_BORDER,
  LEDGER_RIGHT_ALIGNED_STYLE,
  fetchProject,
  getEmptySectionTableDataByHeader,
} from '../../../utils';

const TITLE_CELL_FULL_WIDTH = 290;
const TITLE_CELL_NARROW_WIDTH = 200;
const RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH = 75;

export const KsAdditionalInfoSection: FC<
  LedgerKsAdditionalInfoSectionProps
> = ({ project }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const accountingTypeOptions = useAccountingTypeOptions();
  const fundBudgetTypeOptions = fundBudgetType.options.map((value) => ({
    value,
    label: FundBudgetTypeLabelModifier[value],
  }));

  const nationalTreasuryReturnBackgroundsTableData = useMemo<{
    header: ReportTableProps['header'];
    rowData: ReportTableProps['data'];
  }>(() => {
    const header: ReportTableProps['header'] = [
      {
        type: 'text',
        value: t('fiscal-year-of-national-treasury-return'),
        ratio: 10,
      },
      {
        type: 'text',
        value: `${t('amount-of-national-treasury-return')}\n${t('report-amount-unit-description')}`,
        ratio: 10,
      },
      {
        type: 'text',
        value: t('reason-of-national-treasury-return'),
        ratio: 48.5,
      },
    ];

    const hasNationalTreasuryReturnBackgrounds =
      project?.fundAdditionalAttributes?.nationalTreasuryReturnBackgrounds &&
      project.fundAdditionalAttributes.nationalTreasuryReturnBackgrounds
        .length > 0;

    const data =
      project?.fundAdditionalAttributes?.nationalTreasuryReturnBackgrounds?.map(
        (d) => {
          return {
            row: [
              { type: 'text', value: d.fiscalYear },
              {
                type: 'text',
                value: formatValueByUnit(d.amount, {
                  operation: 'round',
                  unit,
                }),
                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
              },
              { type: 'text', value: d.reasons },
            ] as ReportTableCell[],
          };
        },
      ) ?? [];

    return {
      header,
      rowData: hasNationalTreasuryReturnBackgrounds
        ? data
        : getEmptySectionTableDataByHeader(header),
    };
  }, [project?.fundAdditionalAttributes, t, unit]);

  return (
    <ReportSection
      title={t('fund-additional-attributes')}
      items={[
        {
          subtitle: t('composition-backgrounds'),
          item: (
            <>
              {project?.fundAdditionalAttributes?.compositionBackgrounds &&
              project.fundAdditionalAttributes.compositionBackgrounds.length >
                0 ? (
                project.fundAdditionalAttributes.compositionBackgrounds.map(
                  (compositionBackground, index, arr) => (
                    <CompositionBackgroundsItem
                      // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                      key={index}
                      isFirstElement={index === 0}
                      isLastElement={index === arr.length - 1}
                      compositionBackground={compositionBackground}
                      accountingTypeOptions={accountingTypeOptions.data ?? []}
                      fundBudgetTypeOptions={fundBudgetTypeOptions}
                    />
                  ),
                )
              ) : (
                <CompositionBackgroundsItem
                  isFirstElement={true}
                  isLastElement={true}
                  compositionBackground={null}
                  accountingTypeOptions={accountingTypeOptions.data ?? []}
                  fundBudgetTypeOptions={fundBudgetTypeOptions}
                />
              )}
            </>
          ),
          wrapperStyle: { breakInside: 'auto' },
        },
        {
          subtitle: t('budget-measure-for-fund'),
          item: (
            <Flex vertical>
              <ReportSectionItemContent
                label={t('classification-of-the-fund')}
                value={
                  project?.fundAdditionalAttributes?.fundBudgetClassification &&
                  FundBudgetClassificationModifier[
                    project?.fundAdditionalAttributes?.fundBudgetClassification
                  ]
                }
                labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
                wrapperStyle={{ borderBottom: LEDGER_BORDER }}
              />
              <NestedContentItem
                labelWidth={TITLE_CELL_NARROW_WIDTH}
                mainRowData={[
                  {
                    label: t('latest-budget-year'),
                    value:
                      project?.fundAdditionalAttributes
                        ?.recentBudgetMeasureYear,
                  },
                ]}
                subRowsData={[
                  {
                    label: t('budget-reason-and-outcome'),
                    value:
                      project?.fundAdditionalAttributes
                        ?.budgetReasonWithAchievementStatusAndReviewResult,
                  },
                ]}
              />
              <ReportSectionItemContent
                isLast
                label={t('next-budget-review-year')}
                value={
                  project?.fundAdditionalAttributes?.nextBudgetMeasureReviewYear
                }
                labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
              />
            </Flex>
          ),
          wrapperStyle: { breakInside: 'auto' },
        },
        {
          subtitle: t('national-treasury-return-backgrounds'),
          item: (
            <ReportSectionItemContent
              isTable
              value={
                <ReportSectionItemTable
                  header={nationalTreasuryReturnBackgroundsTableData.header}
                  data={nationalTreasuryReturnBackgroundsTableData.rowData}
                />
              }
            />
          ),
        },
        {
          subtitle: t(
            'effort-and-result-overview-of-reviews-carried-out-in-the-past',
          ),
          item: (
            <ReportSectionItemContent
              isLast
              value={
                project?.fundAdditionalAttributes?.effortAndResultAndOverview
              }
            />
          ),
        },
        {
          subtitle: t('matter-of-act-for-normalization-of-grant'),
          item: (
            <ReportSectionItemContent
              isLast
              value={
                project?.fundAdditionalAttributes
                  ?.matterOfActForNormalizationOfGrant
              }
            />
          ),
        },
      ]}
    />
  );
};

const CompositionBackgroundsItem = ({
  isFirstElement,
  isLastElement,
  compositionBackground,
  accountingTypeOptions,
  fundBudgetTypeOptions,
}: {
  isFirstElement: boolean;
  isLastElement: boolean;
  compositionBackground: z.infer<typeof FundCompositionBackgroundsModel> | null;
  accountingTypeOptions: { value: string; label: string }[];
  fundBudgetTypeOptions: { value: string; label: string }[];
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const [relatedProjectName, setRelatedProjectName] = useState<string | null>(
    compositionBackground?.relatedReviewSheetName ?? null,
  );

  const fetchRelatedProjectName = async () => {
    if (!compositionBackground?.relatedReviewSheetId) {
      return;
    }
    try {
      const relatedProject = await fetchProject(
        compositionBackground.relatedReviewSheetId,
      );
      setRelatedProjectName(relatedProject?.name);
    } catch {
      return null;
    }
  };

  const basicTableData = useMemo(() => {
    const header: ReportTableProps['header'] = [
      {
        type: 'text',
        value: t('fiscal-year-of-budgetary-provision'),
        ratio: 40,
      },
      {
        type: 'text',
        value: isFirstElement
          ? t('fiscal-year-of-creation')
          : t('added-fiscal-year'),
        ratio: 40,
      },
      {
        type: 'text',
        value: t('means-of-delivery'),
        ratio: 57,
      },
      {
        type: 'text',
        value: t('accounting-type'),
        ratio: 81,
      },
      {
        type: 'text',
        value: t('fund-budget-type'),
        ratio: 56,
      },
    ];

    const rowData: ReportTableProps['data'] = [
      {
        row: [
          {
            type: 'text',
            value:
              compositionBackground?.fiscalYearOfBudgetaryProvision ?? null,
          },
          {
            type: 'text',
            value: compositionBackground?.fiscalYearOfCreation ?? null,
          },
          {
            type: 'text',
            value: compositionBackground?.meansOfDelivery
              ? MeansOfDeliveryLabelModifier[
                  compositionBackground.meansOfDelivery
                ]
              : null,
          },
          {
            type: 'text',
            value: compositionBackground?.accountingId
              ? (accountingTypeOptions.find(
                  (type) => type.value === compositionBackground.accountingId,
                )?.label ?? null)
              : null,
          },
          {
            type: 'text',
            value: compositionBackground?.budget
              ? (fundBudgetTypeOptions.find(
                  (type) => type.value === compositionBackground.budget,
                )?.label ?? null)
              : null,
          },
        ] as ReportTableCell[],
      },
    ];

    return {
      header,
      rowData,
    };
  }, [
    t,
    compositionBackground,
    accountingTypeOptions,
    fundBudgetTypeOptions,
    isFirstElement,
  ]);

  const relatedReviewSheetData = useMemo<{
    year: number | null | undefined;
    projectName: string | null;
  }>(() => {
    return {
      year: compositionBackground?.relatedReviewSheetYear,
      projectName: relatedProjectName,
    };
  }, [compositionBackground, relatedProjectName]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    fetchRelatedProjectName();
  }, []);

  return (
    <Flex
      vertical
      style={{
        breakInside: 'avoid',
        borderTop: isFirstElement ? 0 : LEDGER_BORDER,
        marginTop: isFirstElement ? 0 : 2,
      }}
    >
      <ReportSectionItemContent
        isTable
        value={
          <ReportSectionItemTable
            header={basicTableData.header}
            data={basicTableData.rowData}
          />
        }
      />

      <ReportSectionItemContent
        label={t('expenditure-budget-name')}
        value={
          compositionBackground?.expenditureBudgetPurposes &&
          compositionBackground.expenditureBudgetPurposes.length > 0
            ? compositionBackground.expenditureBudgetPurposes
                .map((d) => {
                  return [
                    d.jurisdiction.name,
                    d.accountingType.type === 'general'
                      ? d.organization?.name
                      : d.accountingType.name,
                    d.expenditurePurpose.upper?.name ?? '',
                    d.expenditurePurpose.name,
                  ].join(NEST_ITEM_CONNECTOR);
                })
                .join('\n')
            : null
        }
        wrapperStyle={{
          borderTop: LEDGER_BORDER,
          borderBottom: LEDGER_BORDER,
          breakInside: 'auto',
        }}
        labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
      />
      <ReportSectionItemContent
        label={t('expenditure-budget-name-for-old')}
        value={compositionBackground?.expenditurePurposeName}
        labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
        wrapperStyle={{ breakInside: 'auto' }}
      />

      <Flex>
        <ReportSectionItemContent
          label={
            t('amount-of-state-expenditure') +
            t('report-amount-unit-description')
          }
          value={
            compositionBackground?.amountOfStateExpenditure
              ? formatValueByUnit(
                  compositionBackground.amountOfStateExpenditure,
                  {
                    operation: 'round',
                    unit,
                  },
                )
              : null
          }
          labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
          valueWrapperStyle={
            compositionBackground?.amountOfStateExpenditure
              ? LEDGER_RIGHT_ALIGNED_STYLE
              : {
                  justifyContent: 'flex-start',
                }
          }
        />
        <ReportSectionItemContent
          label={t('have-applied-law-of-appropriateness-of-subsidies')}
          value={
            compositionBackground?.haveAppliedLawOfAppropriatenessOfSubsidies
              ? formatExistenceValue(
                  compositionBackground.haveAppliedLawOfAppropriatenessOfSubsidies,
                )
              : FALLBACK_TEXT
          }
          labelStyle={{ width: TITLE_CELL_NARROW_WIDTH }}
          wrapperStyle={{ borderLeft: LEDGER_BORDER }}
        />
      </Flex>

      <Flex
        style={{
          width: '100%',
          borderBottom: isLastElement ? 'none' : LEDGER_BORDER,
          breakInside: 'avoid-page',
        }}
      >
        <ReportSectionItemContent
          label={t('related-review-sheet')}
          isLast
          value={
            <ReportSectionItemContent
              isLast
              label={t('created-year')}
              value={relatedReviewSheetData.year}
              wrapperStyle={{
                borderLeft: 0,
              }}
              labelStyle={{
                borderLeft: 0,
                width: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
                minWidth: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
              }}
              valueWrapperStyle={{
                width: 72,
              }}
            />
          }
          labelStyle={{ width: TITLE_CELL_FULL_WIDTH }}
        />
        <ReportSectionItemContent
          isLast
          label={t('project-name')}
          value={relatedReviewSheetData.projectName}
          labelStyle={{
            width: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
            minWidth: RELATED_REVIEW_SHEET_TITLE_CELL_WIDTH,
          }}
          wrapperStyle={{ borderLeft: LEDGER_BORDER }}
        />
      </Flex>
    </Flex>
  );
};
